/**
 * Sanitise an untrusted `?redirect=` query param so the SPA only navigates
 * to same-origin, root-anchored paths. Defeats open-redirect, protocol-
 * relative (`//evil`), backslash (`/\evil`), CRLF/control-char and
 * percent-encoded-bypass attacks. Anything not provably safe — including
 * overlong paths — falls back to '/'.
 *
 * Kept in lockstep with the server copy `sanitiseRedirectPath` in
 * api/src/routes/portal/auth.ts (same predicate, different default shape).
 * The 512 cap mirrors the `portal_login_links.redirect_path VARCHAR(512)`
 * column so the value the SPA keeps is the same one the token can bind.
 */
const MAX_REDIRECT_PATH_LENGTH = 512;

/** True if `s` contains any C0 control char (incl. TAB/CR/LF) or DEL. */
function hasControlChars(s: string): boolean {
  for (let i = 0; i < s.length; i += 1) {
    const code = s.charCodeAt(i);
    if (code < 0x20 || code === 0x7f) return true;
  }
  return false;
}

export function sanitiseRedirect(raw: string | null): string {
  if (!raw) return '/';
  if (raw.length > MAX_REDIRECT_PATH_LENGTH) return '/';
  if (hasControlChars(raw)) return '/';
  if (raw.includes('\\')) return '/';
  if (!raw.startsWith('/')) return '/';
  if (raw.startsWith('//')) return '/';
  if (raw.startsWith('/login')) return '/';
  // Percent-encoded bypass guard: decode once and re-check the dangerous
  // prefixes/chars (e.g. '/%2f%2fevil', '/%5cevil', '/foo%0d%0a...').
  let decoded: string;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    return '/'; // malformed percent-encoding
  }
  if (decoded !== raw) {
    if (hasControlChars(decoded)) return '/';
    if (decoded.includes('\\')) return '/';
    if (decoded.startsWith('//')) return '/';
  }
  return raw;
}
